import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import { Stack, Text } from "@certn/cascadia";
import {
  Container,
  SectionMessage,
  TagGroupSelectFormikField,
  Popover,
  DialogTrigger,
  LinkButton,
  PopoverContent,
  Divider,
  EmailSelectorField,
  TextAreaFormikField,
  Text as StepHeadingText,
} from "@certn/cascadia-react";
import { regexTypes, useTimeTracking } from "@certn/utils";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext, useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { array, object, string } from "yup";

import { EmailListField } from "./EmailListField";
import { NotificationMethodsField } from "./recipients/NotificationMethodsField";
import { RecipientsTableField } from "./recipients/RecipientsTableField";
import { AIMessageGeneratorFormikField } from "../../../components/ai-message-generator/AIMessageGeneratorFormikField";
import { ClientJsonForm } from "../../../components/client-json-form/ClientJsonForm";
import { RescreenFrequencyDropdownFormik } from "../../../components/re-screen/RescreenFrequencyDropdownFormik";
import { OrderContext } from "../OrderContext";
import { OrderStepLayout } from "../components/OrderStepLayout";
import {
  enabledNotificationChannels,
  orderContextToGetClientFormPayload,
  selectedEnabledNotificationChannels,
} from "../order-utils";
import { SelectLanguageFormikField } from "../../../components/select-language-formik-field/SelectLanguageFormikField";
import { useCanOrderCVReview } from "../../../components/cv-review/utils";
import { CVReviewUpload } from "../../../components/cv-review/CVReviewUpload";
import { useFocusStepHeading } from "../useFocusStepHeading";

export function OrderDetailsStep() {
  const { t } = useTranslation();
  const stepHeadingId = useFocusStepHeading();
  const { order, setOrder, goToNextStep } = useContext(OrderContext);
  const [validateJsonForm, setValidateJsonForm] = useState(false);
  const [disableCompletion, setDisableCompletion] = useState<boolean>(false);
  const { data: tags } = useClientPortalAPI(
    `/api/internal/client-portal/tags/active/`
  );
  const {
    clientPortalCaseTags,
    clientPortalNewRecipientEmailSelectorOrderFlow,
    clientPortalGenerativeAiForAdditionalMessageOrderFlow,
    clientPortalEnableLanguageSelectField,
    applicantSmsMessaging,
    applicantWhatsappMessaging,
  } = useFlags();
  const messagingChannels = enabledNotificationChannels({
    applicantSmsMessaging,
    applicantWhatsappMessaging,
  });
  const showNotificationMethods = messagingChannels.length > 0;

  const { canOrderCVReview, checks } = useCanOrderCVReview({
    regularChecks: order.checks,
    packageChecks: order.packageData?.checks || [],
  });

  const { startTimeTracker, stopTimeTracker } = useTimeTracking();

  const allowedLanguages = ["en-CA", "fr-CA", "en-GB", "en-US", "en-AU"];

  const formSchema = object().shape({
    recipients: array()
      .min(1, t("This cannot be blank."))
      .max(100, t("You cannot have more than 100 recipients."))
      .test(
        "invalidEmail",
        t("One or more entries is not using a valid email format."),
        (inputStringArray) => {
          return inputStringArray?.every((item) => {
            return string()
              .email()
              .matches(regexTypes.checkMultipleEmails)
              .isValidSync(item);
          });
        }
      )
      .test("uniqueOnly", t("Every email must be unique."), (list) => {
        return list?.length === new Set(list).size;
      }),
    recipient_mobile_numbers: object().test(
      "validMobileNumbers",
      t("Please enter a valid phone number."),
      function (value) {
        const channels = selectedEnabledNotificationChannels(
          this.parent.notification_channels,
          messagingChannels
        );
        if (channels.length === 0) {
          return true;
        }
        const recipients: string[] = this.parent.recipients ?? [];
        const numbers = value as
          | Record<string, { status?: string } | undefined>
          | undefined;
        return recipients.every(
          (email) => numbers?.[email]?.status !== "invalid"
        );
      }
    ),
    message: string(),
    // The client json form will validate its own data. This is just to block submitting the form.
    clientFormDataAndErrors: object()
      .shape({
        errors: array().length(0),
      })
      .required(),
    applicant_language: string()
      .oneOf(allowedLanguages, t("Invalid language"))
      .nullable(),
  });

  const { checks: selectedChecks } = order;
  const hasQuestionnaire = selectedChecks?.some(
    (check) => check.key === "QUESTIONNAIRE_1"
  );
  // This combines the existing tags from the api and what is in the order context
  // then makes a unique list so that the step backward works
  const tagsList = tags?.concat(order.tags);
  const uniqueTagsMap = new Map(tagsList?.map((tag) => [tag.id, tag]));
  const uniqueTagsList = [...uniqueTagsMap.values()];

  useEffect(() => {
    // only set this when the page loads the first time

    startTimeTracker("order--order-details-step");
  }, [startTimeTracker]);

  return (
    <Formik
      initialValues={{
        ...order,
        notification_channels: order.notification_channels ?? [],
        recipient_mobile_numbers: order.recipient_mobile_numbers ?? {},
      }}
      onSubmit={(values) => {
        // Bundles can be pruned while this form is open; keep the current ones.
        setOrder((prev) => ({ ...values, bundles: prev.bundles }));
        goToNextStep();
      }}
      validationSchema={formSchema}
    >
      {({
        handleSubmit,
        isSubmitting,
        values,
        setTouched,
        setFieldValue,
        validateForm,
      }) => {
        return (
          <OrderStepLayout
            onComplete={() => {
              setValidateJsonForm(true);
              setTouched(
                Object.keys(values).reduce((acc, key) => {
                  if (key === "clientFormDataAndErrors") return acc;
                  return {
                    ...acc,
                    [key]: true,
                  };
                }, {})
              );
              void validateForm().then((errors) => {
                if (Object.keys(errors).length === 0) {
                  stopTimeTracker("order--order-details-step");
                  handleSubmit();
                }
              });
            }}
            submitting={isSubmitting}
            disableCompletion={
              disableCompletion ||
              (canOrderCVReview && values.recipients.length !== 1)
            }
          >
            <Container
              layout="narrow"
              testID="order-details-page-outermost-container"
            >
              <StepHeadingText
                id={stepHeadingId}
                as="h1"
                tabIndex={-1}
                className={styles.titleSpacing}
                style="global/title-small"
              >
                {t("Order details")}
              </StepHeadingText>
              {/* For the time being, the form is separated into a Formik form and a JsonForm,
              because the JsonForm is used to submit claims in the backend. Later, we can synthesize
              into one multi-purpose form. */}
              <ClientJsonForm
                isSubmitted={validateJsonForm}
                clientFormPayload={orderContextToGetClientFormPayload(order)}
                questionsUrl={`/api/internal/client-portal/customer-groups/${order.group}/order-questions/`}
                isApplyLink={false}
              />

              {values.recipients.length > 100 && (
                <SectionMessage
                  type="danger"
                  sectionTitle={t("Maximum quantity exceeded")}
                  icon="error"
                  testID="recipients-page-section-message-error"
                >
                  {t(
                    "You have exceeded the limit of 100 recipients in a single order. Please reduce the number of recipients and try again."
                  )}
                </SectionMessage>
              )}
              <div className={styles.mainContentContainer}>
                <section>
                  {showNotificationMethods ? (
                    <Stack gap="space-5">
                      <h2 className={styles.heading}>
                        <Text style="global/heading-medium">
                          {t("Recipients")}
                        </Text>
                      </h2>
                      <NotificationMethodsField
                        smsEnabled={Boolean(applicantSmsMessaging)}
                        whatsappEnabled={Boolean(applicantWhatsappMessaging)}
                      />
                      <RecipientsTableField
                        description={
                          canOrderCVReview
                            ? t(
                                "Enter the applicant’s email address to send the screening invitation."
                              )
                            : t(
                                "To add multiple emails at once, separate them with a space, comma or semi-colon."
                              )
                        }
                        enabledChannels={messagingChannels}
                        max={canOrderCVReview ? 1 : undefined}
                      />
                    </Stack>
                  ) : clientPortalNewRecipientEmailSelectorOrderFlow ? (
                    <EmailSelectorField
                      description={
                        canOrderCVReview
                          ? t(
                              "Enter the applicant’s email address to send the screening invitation."
                            )
                          : t(
                              "Tip: You can paste multiple emails into the field above. Please separate each email with a space, comma or semi-colon"
                            )
                      }
                      isRequired
                      label={t("Send to")}
                      name="recipients"
                      testID="applicants-email-list"
                      max={canOrderCVReview ? 1 : undefined}
                    />
                  ) : (
                    <>
                      <Text
                        style="global/heading-medium"
                        testID="number-of-recipients"
                      >
                        {t("Recipients ({{length}})", {
                          length: values.recipients.length,
                        })}
                      </Text>
                      <EmailListField
                        label={t("Recipient email(s)")}
                        name="recipients"
                        isRequired
                        testID="enter-applicants-page-recipient-emails"
                        description={t(
                          "To send to multiple recipients, separate each email with a comma or space."
                        )}
                      />
                    </>
                  )}
                </section>
                {canOrderCVReview && (
                  <CVReviewUpload
                    customerGroupId={order.group}
                    initialCvReviewId={values.cv_review_id}
                    onCVReviewChange={(cv_review_id) => {
                      setFieldValue("cv_review_id", cv_review_id);
                      setOrder((prev) => ({ ...prev, cv_review_id }));
                    }}
                    setDisableCompletion={setDisableCompletion}
                    checks={checks}
                  />
                )}

                {clientPortalEnableLanguageSelectField && (
                  <SelectLanguageFormikField
                    description={t(
                      "Sets the language for applicant emails, reports, and the portal experience."
                    )}
                  />
                )}
                <section>
                  {clientPortalGenerativeAiForAdditionalMessageOrderFlow ? (
                    <AIMessageGeneratorFormikField
                      name="message"
                      label={t("Additional message")}
                      testID="additional-message-field"
                      description={t(
                        "This will be displayed to the applicant in the invite email."
                      )}
                    />
                  ) : (
                    <Stack gap="space-2">
                      <TextAreaFormikField
                        name="message"
                        label={t("Additional message")}
                        testID="enter-applicants-page-additional-message-input"
                        maxLength={1200}
                        description={t(
                          "This will be displayed to the applicant in the invite email."
                        )}
                      />
                    </Stack>
                  )}
                </section>
                <Divider />
                <Text
                  style="global/heading-medium"
                  testID="case-details-heading"
                >
                  {t("Case details")}
                </Text>
                <section>
                  {tags && clientPortalCaseTags && (
                    <>
                      <TagGroupSelectFormikField
                        label={t("Tags")}
                        name="tags"
                        testID="tag-select-field"
                        values={uniqueTagsList || []}
                        selectedValues={order.tags}
                        setSelectedValues={() => undefined}
                        info={
                          <DialogTrigger>
                            <LinkButton testID="">
                              {t("What's this?")}
                            </LinkButton>
                            <Popover placement="top right">
                              <PopoverContent>
                                <Trans>
                                  Tags allow you to categorize and identify
                                  themes in your orders. Tags can be managed
                                  within the group settings and they will be
                                  visible to all members within the group.
                                  Applicants will not see this.
                                </Trans>
                              </PopoverContent>
                            </Popover>
                          </DialogTrigger>
                        }
                      />
                    </>
                  )}
                </section>
                {
                  <>
                    <Divider />
                    <Text
                      style="global/heading-medium"
                      testID="automation-heading"
                    >
                      {t("Automation")}
                    </Text>
                    <section>
                      <RescreenFrequencyDropdownFormik />
                    </section>
                  </>
                }
                {hasQuestionnaire && (
                  <>
                    <Divider />
                    <Text style="form/body-secondary">
                      {t(
                        "By ordering the Questionnaire, you acknowledge and agree that Certn will not be held liable for your failure to comply with your legal obligations (including, without limitation, under privacy and human rights laws) as a property manager."
                      )}
                    </Text>
                  </>
                )}
              </div>
            </Container>
          </OrderStepLayout>
        );
      }}
    </Formik>
  );
}

const styles = {
  heading: css({ margin: 0 }),
  titleSpacing: css({ marginBottom: "calc(-1 * var(--space-5))" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
  }),
};
